import React from 'react';
import { connect } from 'react-redux';
import { FormattedTime, FormattedDate, FormattedMessage } from 'react-intl';
import ExclamationPointIcon from '../svg/ExclamationPointIcon';
import { loadMessageDetails } from '../../redux/actions/messagesActions';
import { Button, buttonTypes, buttonStyles } from '../shared/Button';

/**
 * Component to display a single message in the messages list
 * @param {Object} props
 */
export const UnconnectedMessagesMessageListItem = (props) => {
  const { displayMessage, openMessage, index } = props;

  let activeStyle = '';
  // If  a message is open...
  if (openMessage) {
    // Check to see if the message type and id match the message displayed in this list item and set the "selected style to it"
    if (
      openMessage.msgType === displayMessage.type &&
      openMessage.msgId === displayMessage.msgId
    ) {
      activeStyle = 'message-center__message-list-item--selected';
    }
  }

  // For every other item in the list add a style to change the background for easier viewing
  let altStyle = '';
  if (index % 2) {
    altStyle = 'message-center__message-list-item--alt';
  }

  /**
   * Generic function to trigger which message to view in the full view
   * @fires props.viewMessage - Updates the MessageCenter local state with the message to show in the full view
   * @fires dispatch#loadMessageDetails - Triggers the api to fetch the message response details to populate the display table
   */
  const handleClick = () => {
    props.viewMessage({
      msgType: displayMessage.type,
      msgId: displayMessage.msgId,
      detailsId: displayMessage.id,
    });
    props.dispatch(loadMessageDetails(displayMessage.id));
  };

  // If the displayed message doesn't have a title, show a placeholder
  let messageTitle = (
    <FormattedMessage
      id="messagesMessageList.messageItemPlaceholderTitle"
      defaultMessage="No Title"
      description="A placeholder title that just says there is no title"
    />
  );
  if (displayMessage.title) {
    messageTitle = displayMessage.title;
  }

  // The actual contents to display inside of the component
  // To have the functionality of being able to click anywhere to select the message, the entire item is a button
  // These are the contents that are inserted into the button
  const contents = (
    <div className="message-list-item__main-container">
      <h2 className="no-margin text-large text-bold" data-test="message-title">
        {messageTitle}
      </h2>
      <p className="no-margin">
        <FormattedDate
          value={new Date(displayMessage.startTime)}
          year="numeric"
          month="2-digit"
          day="2-digit"
        />
        <span className="margin-left-5">
          <FormattedTime
            value={new Date(displayMessage.startTime)}
            timeZone="UTC"
          />
        </span>
      </p>
      <p
        className="no-margin message-list-item__message-body text-medium-small"
        data-test="message-text">
        {displayMessage.message}
      </p>
      <div className="message-list-item__message-extra-info">
        <p
          className="no-margin text-bold text-medium"
          data-test="message-status">
          {displayMessage.messageStatus}
        </p>
        <FormattedMessage
          id="messagesMessageList.messageItemRecipientCountTitle"
          defaultMessage="Recipient Count:"
          description="The number of recipients for the message"
          data-test="recipient-count">
          {(txt) => (
            <span
              className="text-bold text-medium"
              data-test="recipient-count-text">
              {txt} {displayMessage.recipientCount}
            </span>
          )}
        </FormattedMessage>
      </div>
    </div>
  );

  return (
    <Button
      text={contents}
      additionalStyles={
        'message-center__message-list-item no-margin ' +
        altStyle +
        ' ' +
        activeStyle
      }
      leftIcon={
        displayMessage.urgent ? (
          <ExclamationPointIcon data-test="urgent-icon" />
        ) : (
          ''
        )
      }
      buttonType={buttonTypes.noButton}
      buttonStyle={buttonStyles.flex}
      leftIconStyle="message-list-item__urgent-container text-extra-large"
      onClick={handleClick}
      data-test="item-button"
    />
  );
};

const mapStateToProps = (state, ownProps) => {
  const message = ownProps.message;
  let displayMessage;
  if (message.type === 'quickMessages') {
    displayMessage = state.messages.quickMessages[message.msgId];
  } else if (message.type === 'alertMessages') {
    displayMessage = state.messages.alertMessages[message.msgId];
  }

  return {
    displayMessage,
  };
};

export default connect(mapStateToProps)(UnconnectedMessagesMessageListItem);
